"use client";

import type React from "react";

import { useState, useEffect, type ChangeEvent, type FormEvent } from "react";

import {
  Box,
  Container,
  Flex,
  Heading,
  Text,
  Button,
  FormControl,
  FormLabel,
  Input,
  SimpleGrid,
  Card,
  CardBody,
  CardHeader,
  VStack,
  HStack,
  Alert,
  AlertIcon,
  Spinner,
  Center,
  Link as ChakraLink,
  IconButton,
  Image,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Icon,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Checkbox,
  Divider,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  List,
  ListItem,
  ListIcon,
  useToast,
} from "@chakra-ui/react";
import {
  FiArrowLeft,
  FiFileText,
  FiDownload,
  FiTrash2,
  FiPlus,
  FiEdit,
  FiAlertTriangle,
  FiCheck,
} from "react-icons/fi";
import { Select } from "chakra-react-select";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import type { Template, DocumentType } from "../types/types";
import { useAuth } from "../contexts/AuthContext";

interface FontMapping {
  original: string;
  mapped: string;
  reason: string;
}

interface SupportedFont {
  family: string;
  category: string;
  variants: string[];
}

export default function TemplateManagement() {
  const { user, session } = useAuth();
  const [templates, setTemplates] = useState<Template[]>([]);
  const [documentTypes, setDocumentTypes] = useState<DocumentType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploadingTemplate, setUploadingTemplate] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const [supportedFonts, setSupportedFonts] = useState<SupportedFont[]>([]);
  const [fontMappings, setFontMappings] = useState<FontMapping[]>([]);
  const [deletingTemplateId, setDeletingTemplateId] = useState<string | null>(
    null
  );
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  // Upload form state
  const [templateName, setTemplateName] = useState("");
  const [selectedDocType, setSelectedDocType] = useState<number | null>(null);
  const [templateFile, setTemplateFile] = useState<File | null>(null);

  // Font styles editing state
  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null);
  const [editedFontStyles, setEditedFontStyles] = useState<any>(null);
  const [savingFontStyles, setSavingFontStyles] = useState(false);
  const {
    isOpen: isFontEditOpen,
    onOpen: onFontEditOpen,
    onClose: onFontEditClose,
  } = useDisclosure();

  // Font mapping warning modal
  const {
    isOpen: isFontWarningOpen,
    onOpen: onFontWarningOpen,
    onClose: onFontWarningClose,
  } = useDisclosure();

  const navigate = useNavigate();

  const portalTarget =
    typeof window !== "undefined" ? document.body : undefined;

  type DocTypeOption = { value: number; label: string };

  const docTypeOptions: DocTypeOption[] = documentTypes.map((d) => ({
    value: d.id,
    label: d.name,
  }));

  const selectedDocTypeOption =
    docTypeOptions.find((o) => o.value === selectedDocType) ?? null;

  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        setError(null);

        if (!user) {
          throw new Error("User not found");
        }

        setUserId(user.id);

        // Fetch supported fonts
        await fetchSupportedFonts();

        const { data: docTypes, error: docTypesError } = await supabase
          .from("document_types")
          .select("*")
          .order("name");

        if (docTypesError) {
          throw docTypesError;
        }

        setDocumentTypes(docTypes || []);

        const { data: userTemplates, error: templatesError } = await supabase
          .from("templates")
          .select(
            `
        *,
        document_type:document_type_id(name, description)
      `
          )
          .eq("user_id", user.id)
          .order("created_at", { ascending: false });

        if (templatesError) {
          throw templatesError;
        }

        setTemplates(userTemplates || []);
      } catch (error: any) {
        console.error("Error fetching data:", error.message);
        setError("Failed to load templates. Please try again.");
      } finally {
        setLoading(false);
      }
    }

    if (user) {
      fetchData();
    }
  }, [user]);

  const fetchSupportedFonts = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL || ""}/supported-fonts`,
        {
          headers: {
            Authorization: `Bearer ${session?.access_token}`,
          },
        }
      );

      if (response.ok) {
        const fonts = await response.json();
        setSupportedFonts(fonts);
      } else {
        console.warn("Failed to fetch supported fonts, using fallback list");
        // Fallback to basic font list
        setSupportedFonts([
          {
            family: "Open Sans",
            category: "sans-serif",
            variants: ["400", "700"],
          },
          {
            family: "Roboto",
            category: "sans-serif",
            variants: ["400", "700"],
          },
          { family: "Lato", category: "sans-serif", variants: ["400", "700"] },
          {
            family: "Montserrat",
            category: "sans-serif",
            variants: ["400", "700"],
          },
          {
            family: "Source Sans Pro",
            category: "sans-serif",
            variants: ["400", "700"],
          },
          {
            family: "Playfair Display",
            category: "serif",
            variants: ["400", "700"],
          },
          {
            family: "Merriweather",
            category: "serif",
            variants: ["400", "700"],
          },
          { family: "PT Serif", category: "serif", variants: ["400", "700"] },
          {
            family: "Crimson Text",
            category: "serif",
            variants: ["400", "700"],
          },
          {
            family: "Source Code Pro",
            category: "monospace",
            variants: ["400", "700"],
          },
        ]);
      }
    } catch (error) {
      console.error("Error fetching supported fonts:", error);
      // Use fallback fonts
      setSupportedFonts([
        {
          family: "Open Sans",
          category: "sans-serif",
          variants: ["400", "700"],
        },
        { family: "Roboto", category: "sans-serif", variants: ["400", "700"] },
        { family: "Lato", category: "sans-serif", variants: ["400", "700"] },
      ]);
    }
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setTemplateFile(e.target.files[0]);
    }
  };

  const validateAndMapFonts = async (fontStyles: any) => {
    if (!fontStyles || supportedFonts.length === 0) {
      return { mappedFontStyles: fontStyles, mappings: [] };
    }

    const mappings: FontMapping[] = [];
    const mappedFontStyles = JSON.parse(JSON.stringify(fontStyles));

    // Helper function to find supported font or map to nearest
    const findSupportedFont = (originalFont: string): string => {
      if (!originalFont) return "Open Sans";

      // Clean the font name (remove quotes, extra spaces)
      const cleanOriginal = originalFont.trim().replace(/['"]/g, "");

      // Check if font is already supported (case-insensitive)
      const isSupported = supportedFonts.some(
        (font) => font.family.toLowerCase() === cleanOriginal.toLowerCase()
      );

      if (isSupported) {
        return cleanOriginal;
      }

      // Map to supported fonts using improved logic
      const lowerFont = cleanOriginal.toLowerCase();
      let mappedFont = "Open Sans"; // default fallback

      if (
        lowerFont.includes("times") ||
        lowerFont.includes("serif") ||
        lowerFont.includes("roman") ||
        lowerFont.includes("cambria")
      ) {
        mappedFont = "PT Serif";
      } else if (
        lowerFont.includes("courier") ||
        lowerFont.includes("mono") ||
        lowerFont.includes("consolas")
      ) {
        mappedFont = "Source Code Pro";
      } else if (
        lowerFont.includes("arial") ||
        lowerFont.includes("helvetica") ||
        lowerFont.includes("segoe")
      ) {
        mappedFont = "Open Sans";
      } else if (
        lowerFont.includes("calibri") ||
        lowerFont.includes("trebuchet")
      ) {
        mappedFont = "Lato";
      } else if (
        lowerFont.includes("georgia") ||
        lowerFont.includes("book antiqua")
      ) {
        mappedFont = "Merriweather";
      } else if (
        lowerFont.includes("verdana") ||
        lowerFont.includes("tahoma") ||
        lowerFont.includes("franklin")
      ) {
        mappedFont = "Roboto";
      } else if (lowerFont.includes("candara")) {
        mappedFont = "Open Sans";
      }

      // Verify the mapped font is in our supported list
      const isMappedSupported = supportedFonts.some(
        (font) => font.family.toLowerCase() === mappedFont.toLowerCase()
      );

      if (!isMappedSupported) {
        mappedFont = "Open Sans"; // ultimate fallback
      }

      // Always add to mappings if the font changed (even slightly)
      if (mappedFont.toLowerCase() !== cleanOriginal.toLowerCase()) {
        mappings.push({
          original: cleanOriginal,
          mapped: mappedFont,
          reason: `"${cleanOriginal}" is not supported and was mapped to a compatible font`,
        });
      }

      return mappedFont;
    };

    // Process all font families in the font styles more thoroughly
    const processSection = (section: any, sectionName: string) => {
      if (section && section.font_family) {
        const originalFont = section.font_family;
        const mappedFont = findSupportedFont(originalFont);
        section.font_family = mappedFont;
      }
    };

    // Process all sections
    if (mappedFontStyles.normal_text) {
      processSection(mappedFontStyles.normal_text, "Normal Text");
    }
    [
      "heading_1",
      "heading_2",
      "heading_3",
      "heading_4",
      "heading_5",
      "heading_6",
    ].forEach((heading) => {
      if (mappedFontStyles[heading]) {
        processSection(
          mappedFontStyles[heading],
          heading.replace("_", " ").toUpperCase()
        );
      }
    });

    return { mappedFontStyles, mappings };
  };

  const handleUploadSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!templateName.trim() || !selectedDocType || !templateFile || !userId) {
      setError("Please fill in all required fields");
      return;
    }

    let currentFontMappings: FontMapping[] = [];

    try {
      setUploadingTemplate(true);
      setError(null);
      setFontMappings([]); // Clear previous mappings

      // First, extract text content if it's a PDF or DOCX file
      let structuralTemplateContent = null;
      let fontStyles = null;
      let extractionMetadata = null;
      const fileExtension = templateFile.name.toLowerCase().split(".").pop();

      if (
        fileExtension === "pdf" ||
        fileExtension === "docx" ||
        fileExtension === "doc"
      ) {
        try {
          // Extract text content
          const textFormData = new FormData();
          textFormData.append("templateFile", templateFile);

          const textResponse = await fetch(
            `${process.env.REACT_APP_SERVER_URL || ""}/extract-template-text`,
            {
              method: "POST",
              headers: {
                Authorization: `Bearer ${session?.access_token}`,
              },
              body: textFormData,
            }
          );

          if (textResponse.ok) {
            const textResult = await textResponse.json();
            structuralTemplateContent = textResult.text;
          } else {
            console.warn(
              "Could not extract text content, will store file only"
            );
          }

          // Extract font styles for PDF and DOCX files
          if (fileExtension === "pdf" || fileExtension === "docx") {
            const fontFormData = new FormData();
            fontFormData.append("templateFile", templateFile);

            const fontResponse = await fetch(
              `${
                process.env.REACT_APP_SERVER_URL || ""
              }/extract-template-fonts`,
              {
                method: "POST",
                headers: {
                  Authorization: `Bearer ${session?.access_token}`,
                },
                body: fontFormData,
              }
            );

            if (fontResponse.ok) {
              const fontResult = await fontResponse.json();
              if (fontResult.success) {
                // Validate and map fonts before saving
                const { mappedFontStyles, mappings } =
                  await validateAndMapFonts(fontResult.fontStyles);
                fontStyles = mappedFontStyles;
                extractionMetadata = fontResult.extractionMetadata;

                // Store mappings in local variable for later use
                currentFontMappings = mappings;
              } else {
                console.warn("Font extraction failed:", fontResult.error);
              }
            } else {
              console.warn("Could not extract font styles");
            }
          }
        } catch (extractError) {
          console.warn(
            "Text/font extraction failed, will store file only:",
            extractError
          );
        }
      } else if (fileExtension === "txt") {
        // For text files, read the content directly
        try {
          structuralTemplateContent = await templateFile.text();
        } catch (textError) {
          console.warn("Could not read text file content:", textError);
        }
      }

      // Upload file to storage
      const fileName = `${userId}/${Date.now()}-${templateFile.name}`;
      const { error: uploadError } = await supabase.storage
        .from("templates")
        .upload(fileName, templateFile);

      if (uploadError) {
        throw new Error(`Error uploading template: ${uploadError.message}`);
      }

      // Create template record with validated font styles
      const templateData: {
        user_id: string;
        document_type_id: number;
        name: string;
        file_path: string;
        structural_template?: string;
        font_styles?: any;
        created_by_extraction?: boolean;
        extraction_metadata?: any;
      } = {
        user_id: userId,
        document_type_id: selectedDocType,
        name: templateName.trim(),
        file_path: fileName,
      };

      // Add structural_template if we successfully extracted text
      if (structuralTemplateContent) {
        templateData.structural_template = structuralTemplateContent;
      }

      // Add validated font styles if we successfully extracted them
      if (fontStyles) {
        templateData.font_styles = fontStyles;
        templateData.created_by_extraction = true;
        templateData.extraction_metadata = extractionMetadata;
      }

      const { data: template, error: templateError } = await supabase
        .from("templates")
        .insert([templateData])
        .select(
          `
        *,
        document_type:document_type_id(name, description)
      `
        )
        .single();

      if (templateError) {
        throw templateError;
      }

      setTemplates([template, ...templates]);

      // Reset form
      setTemplateName("");
      setSelectedDocType(null);
      setTemplateFile(null);
      onClose();

      // Show success message first
      toast({
        title: "Template uploaded successfully",
        description: fontStyles
          ? "Template uploaded with font styles extracted!"
          : "Template uploaded successfully!",
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      // Show font mapping warning if there were any mappings
      if (currentFontMappings.length > 0) {
        setFontMappings(currentFontMappings);
        // Use setTimeout to ensure state is updated before opening modal
        setTimeout(() => {
          onFontWarningOpen();
        }, 100);
      }
    } catch (error: any) {
      console.error("Error uploading template:", error.message);
      setError(`Failed to upload template: ${error.message}`);
    } finally {
      setUploadingTemplate(false);
    }
  };

  const deleteTemplate = async (templateId: string, filePath: string) => {
    if (!window.confirm("Are you sure you want to delete this template?")) {
      return;
    }

    try {
      setDeletingTemplateId(templateId);
      setError(null);

      // First, check if any documents reference this template
      const { data: referencingDocuments, error: checkError } = await supabase
        .from("documents")
        .select("id")
        .eq("template_id", templateId);

      if (checkError) {
        throw new Error(
          `Error checking template references: ${checkError.message}`
        );
      }

      // If there are documents referencing this template, update them to remove the reference
      if (referencingDocuments && referencingDocuments.length > 0) {
        const { error: updateError } = await supabase
          .from("documents")
          .update({ template_id: null })
          .eq("template_id", templateId);

        if (updateError) {
          throw new Error(
            `Error updating document references: ${updateError.message}`
          );
        }
      }

      // Delete file from storage
      const { error: storageError } = await supabase.storage
        .from("templates")
        .remove([filePath]);

      if (storageError) {
        console.warn(
          "Warning: Could not delete file from storage:",
          storageError.message
        );
      }

      // Delete template record
      const { error: dbError } = await supabase
        .from("templates")
        .delete()
        .eq("id", templateId);

      if (dbError) {
        throw dbError;
      }

      // Update local state
      setTemplates(templates.filter((t: Template) => t.id !== templateId));

      toast({
        title: "Template deleted successfully",
        description:
          referencingDocuments && referencingDocuments.length > 0
            ? `Template deleted and removed from ${referencingDocuments.length} document(s)`
            : "Template deleted successfully",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } catch (error: any) {
      console.error("Error deleting template:", error.message);
      setError(`Failed to delete template: ${error.message}`);
      toast({
        title: "Error deleting template",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setDeletingTemplateId(null);
    }
  };

  const downloadTemplate = async (template: Template) => {
    try {
      const { data, error } = await supabase.storage
        .from("templates")
        .download(template.file_path);

      if (error) {
        throw error;
      }

      const url = URL.createObjectURL(data);
      const link = document.createElement("a");
      link.href = url;
      link.download = template.name || `template-${template.id}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error("Error downloading template:", error.message);
      setError("Failed to download template. Please try again.");
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const openFontStylesEditor = (template: Template) => {
    setEditingTemplate(template);
    setEditedFontStyles(
      template.font_styles
        ? JSON.parse(JSON.stringify(template.font_styles))
        : {}
    );
    onFontEditOpen();
  };

  const handleFontStyleChange = (
    category: string,
    property: string,
    value: any
  ) => {
    setEditedFontStyles((prev: any) => ({
      ...prev,
      [category]: {
        ...prev[category],
        [property]: value,
      },
    }));
  };

  const saveFontStyles = async () => {
    if (!editingTemplate || !editedFontStyles) return;

    try {
      setSavingFontStyles(true);
      setError(null);

      const { error: updateError } = await supabase
        .from("templates")
        .update({ font_styles: editedFontStyles })
        .eq("id", editingTemplate.id);

      if (updateError) {
        throw updateError;
      }

      // Update the local templates state
      setTemplates(
        templates.map((t) =>
          t.id === editingTemplate.id
            ? { ...t, font_styles: editedFontStyles }
            : t
        )
      );

      onFontEditClose();
      setEditingTemplate(null);
      setEditedFontStyles(null);

      toast({
        title: "Font styles updated",
        description: "Template font styles have been saved successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error: any) {
      console.error("Error saving font styles:", error);
      setError(`Failed to save font styles: ${error.message}`);
    } finally {
      setSavingFontStyles(false);
    }
  };

  const renderFontStyleEditor = (category: string, styles: any) => {
    if (!styles) return null;

    type FontOption = { value: string; label: string; category: string };

    const fontOptions: FontOption[] = supportedFonts
      .slice()
      .sort((a, b) => a.family.localeCompare(b.family))
      .map((f) => ({
        value: f.family,
        label: `${f.family} (${f.category})`,
        category: f.category,
      }));

    const selectedFont =
      fontOptions.find((o) => o.value === (styles.font_family || "")) ?? null;

    return (
      <VStack spacing={3} align="stretch">
        {styles.font_family && (
          <FormControl>
            <FormLabel fontSize="sm">Font Family</FormLabel>
            <Select<FontOption, false>
              size="sm"
              placeholder="Select Font"
              options={fontOptions}
              value={selectedFont}
              onChange={(opt) =>
                handleFontStyleChange(category, "font_family", opt?.value ?? "")
              }
              // 👇 key bits to escape clipping
              menuPortalTarget={portalTarget as HTMLElement}
              menuPosition="fixed"
              menuPlacement="auto"
              menuShouldScrollIntoView={false}
              chakraStyles={{
                control: (base) => ({
                  ...base,
                  bg: "white",
                  borderColor: "brand.200",
                  _hover: { borderColor: "brand.300" },
                  _focusWithin: {
                    borderColor: "brand.500",
                    boxShadow: "0 0 0 1px var(--chakra-colors-brand-500)",
                  },
                }),
                dropdownIndicator: (base) => ({ ...base, color: "brand.500" }),
                menu: (base) => ({ ...base, zIndex: 2000 }), // keep above panels/modals
                menuList: (base) => ({ ...base, maxHeight: "240px" }),
                option: (base, state) => ({
                  ...base,
                  bg: state.isSelected
                    ? "brand.500"
                    : state.isFocused
                    ? "brand.100"
                    : "white",
                  color: state.isSelected
                    ? "white"
                    : "var(--chakra-colors-gray-900)",
                  _active: { bg: "brand.600", color: "white" },
                }),
              }}
              // react-select style slot for the portal container itself
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 2000 }),
              }}
            />
          </FormControl>
        )}

        {styles.font_size !== undefined && (
          <FormControl>
            <FormLabel fontSize="sm">Font Size (pt)</FormLabel>
            <NumberInput
              size="sm"
              value={styles.font_size || 12}
              min={6}
              max={72}
              onChange={(valueString) =>
                handleFontStyleChange(
                  category,
                  "font_size",
                  Number.parseFloat(valueString) || 12
                )
              }
              variant="outline"
              clampValueOnBlur={false}
            >
              <NumberInputField bg="white" _hover={{ bg: "white" }} />
              <NumberInputStepper bg="transparent">
                <NumberIncrementStepper
                  bg="transparent"
                  color="primary.500"
                  _hover={{ color: "primary.600", bg: "primary.100" }}
                  _active={{ color: "primary.700", bg: "transparent" }}
                  _disabled={{ opacity: 0.4, bg: "transparent" }}
                />
                <NumberDecrementStepper
                  bg="transparent"
                  color="primary.500"
                  _hover={{ color: "primary.600", bg: "primary.100" }}
                  _active={{ color: "primary.700", bg: "transparent" }}
                  _disabled={{ opacity: 0.4, bg: "transparent" }}
                />
              </NumberInputStepper>
            </NumberInput>
          </FormControl>
        )}

        {styles.line_spacing !== undefined && (
          <FormControl>
            <FormLabel fontSize="sm">Line Spacing</FormLabel>
            <NumberInput
              size="sm"
              value={styles.line_spacing || 1.15}
              min={0.5}
              max={3}
              step={0.05}
              onChange={(valueString) =>
                handleFontStyleChange(
                  category,
                  "line_spacing",
                  Number.parseFloat(valueString) || 1.15
                )
              }
              variant="outline"
              clampValueOnBlur={false}
            >
              <NumberInputField bg="white" _hover={{ bg: "white" }} />
              <NumberInputStepper bg="transparent">
                <NumberIncrementStepper
                  bg="transparent"
                  color="primary.500"
                  _hover={{ color: "primary.600", bg: "primary.100" }}
                  _active={{ color: "primary.700", bg: "transparent" }}
                  _disabled={{ opacity: 0.4, bg: "transparent" }}
                />
                <NumberDecrementStepper
                  bg="transparent"
                  color="primary.500"
                  _hover={{ color: "primary.600", bg: "primary.100" }}
                  _active={{ color: "primary.700", bg: "transparent" }}
                  _disabled={{ opacity: 0.4, bg: "transparent" }}
                />
              </NumberInputStepper>
            </NumberInput>
          </FormControl>
        )}

        <HStack spacing={4}>
          {styles.bold !== undefined && (
            <Checkbox
              isChecked={styles.bold || false}
              onChange={(e) =>
                handleFontStyleChange(category, "bold", e.target.checked)
              }
              colorScheme="primary"
              sx={{
                ".chakra-checkbox__control": {
                  borderColor: "primary.500",
                  borderWidth: "1.5px",
                  _focusVisible: {
                    boxShadow: "0 0 0 3px var(--chakra-colors-primary-200)",
                  },
                  _checked: {
                    bg: "primary.200",
                    borderColor: "primary.500",
                  },
                },
                "&:hover .chakra-checkbox__control": {
                  bg: "primary.50",
                  borderColor: "primary.600",
                },
              }}
            >
              <Text fontSize="sm">Bold</Text>
            </Checkbox>
          )}

          {styles.italic !== undefined && (
            <Checkbox
              isChecked={styles.italic || false}
              onChange={(e) =>
                handleFontStyleChange(category, "italic", e.target.checked)
              }
              sx={{
                ".chakra-checkbox__control": {
                  borderColor: "primary.500",
                  borderWidth: "1.5px",
                  _focusVisible: {
                    boxShadow: "0 0 0 3px var(--chakra-colors-primary-200)",
                  },
                  _checked: {
                    bg: "primary.200",
                    borderColor: "primary.500",
                  },
                },
                "&:hover .chakra-checkbox__control": {
                  bg: "primary.50",
                  borderColor: "primary.600",
                },
              }}
            >
              <Text fontSize="sm">Italic</Text>
            </Checkbox>
          )}

          {styles.underline !== undefined && (
            <Checkbox
              isChecked={styles.underline || false}
              onChange={(e) =>
                handleFontStyleChange(category, "underline", e.target.checked)
              }
              sx={{
                ".chakra-checkbox__control": {
                  borderColor: "primary.500",
                  borderWidth: "1.5px",
                  _focusVisible: {
                    boxShadow: "0 0 0 3px var(--chakra-colors-primary-200)",
                  },
                  _checked: {
                    bg: "primary.200",
                    borderColor: "primary.500",
                  },
                },
                "&:hover .chakra-checkbox__control": {
                  bg: "primary.50",
                  borderColor: "primary.600",
                },
              }}
            >
              <Text fontSize="sm">Underline</Text>
            </Checkbox>
          )}
        </HStack>

        {styles.color && (
          <FormControl>
            <FormLabel fontSize="sm">Color</FormLabel>
            <Input
              size="sm"
              type="color"
              value={styles.color || "#000000"}
              onChange={(e) =>
                handleFontStyleChange(category, "color", e.target.value)
              }
            />
          </FormControl>
        )}
      </VStack>
    );
  };

  return (
    <Box minH="100vh" bg="gray.50">
      {/* Header */}
      <Box
        bg="rgb(253, 251, 252)"
        shadow="sm"
        position="sticky"
        top={0}
        zIndex={10}
      >
        <Container maxW="7xl">
          <Flex h={16} align="center" justify="space-between">
            <HStack spacing={4}>
              <Image
                src="/main-logo.png"
                alt="RadiusDocs AI"
                height="50px"
                width="auto"
                objectFit="contain"
              />
            </HStack>

            <HStack spacing={6}>
              <ChakraLink
                as={Link}
                to="/dashboard"
                color="gray.600"
                fontWeight="medium"
              >
                Dashboard
              </ChakraLink>
              <ChakraLink
                as={Link}
                to="/document-history"
                color="gray.600"
                fontWeight="medium"
              >
                Documents
              </ChakraLink>
              <ChakraLink
                as={Link}
                to="/templates"
                color="primary.600"
                fontWeight="medium"
              >
                Templates
              </ChakraLink>
              <ChakraLink
                as={Link}
                to="/billing"
                color="gray.600"
                fontWeight="medium"
              >
                Billing
              </ChakraLink>
            </HStack>

            <Button
              onClick={() => navigate("/dashboard")}
              variant="ghost"
              leftIcon={<Icon as={FiArrowLeft} />}
              colorScheme="primary"
            >
              Back
            </Button>
          </Flex>
        </Container>
      </Box>

      <Container maxW="7xl" py={8}>
        <VStack spacing={6} align="stretch">
          {/* Header Actions */}
          <Flex justify="space-between" align="center">
            <Heading size="xl" color="gray.800">
              Template Management
            </Heading>
            <Button
              onClick={onOpen}
              colorScheme="primary"
              leftIcon={<Icon as={FiPlus} />}
            >
              Upload Template
            </Button>
          </Flex>

          {error && (
            <Alert status="error" borderRadius="md" textColor={"red.600"}>
              <AlertIcon />
              {error}
            </Alert>
          )}

          {/* Templates Grid */}
          {loading ? (
            <Center py={12}>
              <VStack spacing={4}>
                <Spinner size="xl" color="primary.500" />
                <Text>Loading templates...</Text>
              </VStack>
            </Center>
          ) : templates.length > 0 ? (
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
              {templates.map((template: Template) => (
                <Card
                  key={template.id}
                  _hover={{ transform: "translateY(-2px)", shadow: "md" }}
                  transition="all 0.2s"
                >
                  <CardHeader pb={3}>
                    <Flex justify="space-between" align="flex-start">
                      <VStack align="start" spacing={1} flex={1} mr={4}>
                        <Heading size="sm" color="gray.800" noOfLines={2}>
                          {template.name}
                        </Heading>
                        <Text
                          fontSize="sm"
                          color="primary.600"
                          fontWeight="medium"
                        >
                          {template.document_type?.name || "Unknown Type"}
                        </Text>
                      </VStack>
                      <HStack spacing={1}>
                        <IconButton
                          aria-label="Download template"
                          icon={<Icon as={FiDownload} />}
                          size="sm"
                          variant="ghost"
                          colorScheme="green"
                          onClick={() => downloadTemplate(template)}
                        />
                        <IconButton
                          aria-label="Delete template"
                          icon={<Icon as={FiTrash2} />}
                          size="sm"
                          variant="ghost"
                          colorScheme="red"
                          onClick={() =>
                            deleteTemplate(template.id, template.file_path)
                          }
                          isLoading={deletingTemplateId === template.id}
                        />
                      </HStack>
                    </Flex>
                  </CardHeader>

                  <CardBody pt={0}>
                    <VStack align="start" spacing={2}>
                      <Text fontSize="sm" color="gray.600">
                        Created: {formatDate(template.created_at)}
                      </Text>
                      {template.document_type?.description && (
                        <Text fontSize="sm" color="gray.600" noOfLines={3}>
                          {template.document_type.description}
                        </Text>
                      )}
                    </VStack>
                  </CardBody>
                  {template.font_styles && (
                    <Box
                      mt={3}
                      p={3}
                      bg="primary.50"
                      borderRadius="md"
                      borderLeft="4px solid"
                      borderLeftColor="primary.400"
                    >
                      <Flex justify="space-between" align="center" mb={1}>
                        <Text
                          fontSize="xs"
                          fontWeight="semibold"
                          color="primary.700"
                        >
                          Font Styles Extracted
                        </Text>
                        <IconButton
                          aria-label="Edit font styles"
                          icon={<Icon as={FiEdit} />}
                          size="xs"
                          variant="ghost"
                          colorScheme="primary"
                          onClick={() => openFontStylesEditor(template)}
                        />
                      </Flex>
                    </Box>
                  )}
                </Card>
              ))}
            </SimpleGrid>
          ) : (
            <Card>
              <CardBody textAlign="center" py={12}>
                <VStack spacing={4}>
                  <Icon as={FiFileText} boxSize={16} color="gray.400" />
                  <Heading size="md" color="gray.600">
                    No templates found
                  </Heading>
                  <Text color="gray.600">
                    You haven't uploaded any templates yet. Upload your first
                    template to get started.
                  </Text>
                  <Button
                    onClick={onOpen}
                    colorScheme="primary"
                    leftIcon={<Icon as={FiPlus} />}
                  >
                    Upload your first template
                  </Button>
                </VStack>
              </CardBody>
            </Card>
          )}
        </VStack>
      </Container>

      {/* Upload Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent
          backgroundColor={"gray.50"}
          p={6}
          sx={{
            /* WebKit/Blink (Chrome, Edge, Safari) */
            "&::-webkit-scrollbar": {
              width: "10px",
              height: "10px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "white",
              borderRadius: "8px",
              boxShadow: "inset 0 0 0 1px var(--chakra-colors-gray-200)",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "var(--chakra-colors-primary-500)",
              borderRadius: "8px",
              border: "2px solid white", // keeps a clean gap on white track
            },
            "&:hover::-webkit-scrollbar-thumb": {
              backgroundColor: "var(--chakra-colors-primary-600)",
            },
            "&::-webkit-scrollbar-thumb:active": {
              backgroundColor: "var(--chakra-colors-primary-700)",
            },
            "&::-webkit-scrollbar-corner": {
              backgroundColor: "white",
            },

            /* Firefox: first = thumb, second = track */
            scrollbarWidth: "thin",
            scrollbarColor: "var(--chakra-colors-primary-500) white",

            /* (optional) keep options styled */
            "& option": {
              backgroundColor: "white",
              color: "var(--chakra-colors-gray-800)",
            },
          }}
        >
          <ModalHeader>Upload New Template</ModalHeader>
          <ModalCloseButton />
          <Box as="form" onSubmit={handleUploadSubmit}>
            <ModalBody>
              <VStack spacing={4}>
                <FormControl isRequired>
                  <FormLabel>Template Name</FormLabel>
                  <Input
                    type="text"
                    value={templateName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setTemplateName(e.target.value)
                    }
                    placeholder="Enter template name"
                    _placeholder={{ color: "gray.500" }}
                    isDisabled={uploadingTemplate}
                    backgroundColor={uploadingTemplate ? "gray.100" : "white"}
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Document Type</FormLabel>

                  <Select<DocTypeOption, false>
                    placeholder="Select Document Type"
                    options={docTypeOptions}
                    value={selectedDocTypeOption}
                    onChange={(opt) => setSelectedDocType(opt?.value ?? null)}
                    isDisabled={uploadingTemplate}
                    // 👇 same escape hatch
                    menuPortalTarget={portalTarget as HTMLElement}
                    menuPosition="fixed"
                    menuPlacement="auto"
                    menuShouldScrollIntoView={false}
                    chakraStyles={{
                      control: (base) => ({
                        ...base,
                        bg: "white",
                        color: "black",
                        borderColor: "brand.200",
                        _hover: { borderColor: "brand.300" },
                        _focusWithin: {
                          borderColor: "brand.500",
                          boxShadow: "0 0 0 1px var(--chakra-colors-brand-500)",
                        },
                      }),
                      dropdownIndicator: (base) => ({
                        ...base,
                        color: "brand.500",
                      }),
                      menu: (base) => ({ ...base, zIndex: 2000, bg: "white" }),
                      menuList: (base) => ({ ...base, maxHeight: "240px" }),
                      option: (base, state) => ({
                        ...base,
                        bg: state.isSelected
                          ? "brand.500"
                          : state.isFocused
                          ? "brand.100"
                          : "white",
                        color: state.isSelected ? "white" : "black",
                        _active: { bg: "brand.600", color: "white" },
                      }),
                    }}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 2000 }),
                    }}
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Template File</FormLabel>
                  <Box
                    position="relative"
                    borderWidth="1px"
                    borderRadius="md"
                    borderColor="gray.200"
                    p={1}
                    bg="gray.50"
                  >
                    <Input
                      type="file"
                      onChange={handleFileChange}
                      isDisabled={uploadingTemplate}
                      accept=".pdf,.docx,.txt"
                      height="auto"
                      py={1}
                      px={2}
                      border="none"
                      backgroundColor={uploadingTemplate ? "gray.100" : "white"}
                    />
                  </Box>
                  <Text fontSize="xs" color="gray.600" mt={1}>
                    Accepted formats: PDF, DOCX, TXT (Max 200MB)
                  </Text>
                </FormControl>
              </VStack>
            </ModalBody>

            <ModalFooter>
              <Button
                variant="ghost"
                mr={3}
                onClick={onClose}
                isDisabled={uploadingTemplate}
                colorScheme="gray"
                _hover={{ bg: "gray.100" }}
                color={"gray.600"}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                colorScheme="primary"
                isLoading={uploadingTemplate}
                loadingText="Uploading..."
              >
                Upload Template
              </Button>
            </ModalFooter>
          </Box>
        </ModalContent>
      </Modal>

      {/* Font Mapping Warning Modal */}
      <Modal isOpen={isFontWarningOpen} onClose={onFontWarningClose} size="lg">
        <ModalOverlay />
        <ModalContent
          backgroundColor="gray.50"
          sx={{
            /* WebKit/Blink (Chrome, Edge, Safari) */
            "&::-webkit-scrollbar": {
              width: "10px",
              height: "10px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "white",
              borderRadius: "8px",
              boxShadow: "inset 0 0 0 1px var(--chakra-colors-gray-200)",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "var(--chakra-colors-primary-500)",
              borderRadius: "8px",
              border: "2px solid white", // keeps a clean gap on white track
            },
            "&:hover::-webkit-scrollbar-thumb": {
              backgroundColor: "var(--chakra-colors-primary-600)",
            },
            "&::-webkit-scrollbar-thumb:active": {
              backgroundColor: "var(--chakra-colors-primary-700)",
            },
            "&::-webkit-scrollbar-corner": {
              backgroundColor: "white",
            },

            /* Firefox: first = thumb, second = track */
            scrollbarWidth: "thin",
            scrollbarColor: "var(--chakra-colors-primary-500) white",

            /* (optional) keep options styled */
            "& option": {
              backgroundColor: "white",
              color: "var(--chakra-colors-gray-800)",
            },
          }}
        >
          <ModalHeader>
            <HStack spacing={2}>
              <Icon as={FiAlertTriangle} color="orange.500" />
              <Text>Font Compatibility Notice</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Text color="gray.700">
                Some fonts in your template are not supported by this
                application right now and have been automatically mapped to
                compatible alternatives:
              </Text>

              <List spacing={2}>
                {fontMappings.map((mapping, index) => (
                  <ListItem key={index}>
                    <HStack spacing={2} align="start">
                      <ListIcon as={FiCheck} color="green.500" mt={1} />
                      <VStack align="start" spacing={0}>
                        <Text fontSize="sm">
                          <Text as="span" fontWeight="semibold" color="red.600">
                            "{mapping.original}"
                          </Text>
                          {" → "}
                          <Text
                            as="span"
                            fontWeight="semibold"
                            color="green.600"
                          >
                            "{mapping.mapped}"
                          </Text>
                        </Text>
                      </VStack>
                    </HStack>
                  </ListItem>
                ))}
              </List>

              <Alert status="info" borderRadius="md">
                <AlertIcon />
                <Text fontSize="sm">
                  You can edit these font selections and select a more
                  appropriate option if needed, by clicking the "Pencil Icon"
                  next to the "Font Styles Extracted" label in the template
                  card.
                </Text>
              </Alert>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="primary" onClick={onFontWarningClose}>
              Got it, thanks!
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Font Styles Editor Modal */}
      <Modal isOpen={isFontEditOpen} onClose={onFontEditClose} size="4xl">
        <ModalOverlay />
        <ModalContent
          backgroundColor="gray.50"
          maxH="90vh"
          sx={{
            /* WebKit/Blink (Chrome, Edge, Safari) */
            "&::-webkit-scrollbar": {
              width: "10px",
              height: "10px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "white",
              borderRadius: "8px",
              boxShadow: "inset 0 0 0 1px var(--chakra-colors-gray-200)",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "var(--chakra-colors-primary-500)",
              borderRadius: "8px",
              border: "2px solid white", // keeps a clean gap on white track
            },
            "&:hover::-webkit-scrollbar-thumb": {
              backgroundColor: "var(--chakra-colors-primary-600)",
            },
            "&::-webkit-scrollbar-thumb:active": {
              backgroundColor: "var(--chakra-colors-primary-700)",
            },
            "&::-webkit-scrollbar-corner": {
              backgroundColor: "white",
            },

            /* Firefox: first = thumb, second = track */
            scrollbarWidth: "thin",
            scrollbarColor: "var(--chakra-colors-primary-500) white",

            /* (optional) keep options styled */
            "& option": {
              backgroundColor: "white",
              color: "var(--chakra-colors-gray-800)",
            },
          }}
        >
          <ModalHeader>Edit Font Styles - {editingTemplate?.name}</ModalHeader>
          <ModalCloseButton />
          <ModalBody overflowY="auto">
            {editedFontStyles && (
              <Tabs variant="enclosed" colorScheme="primary">
                <TabList>
                  <Tab>Normal Text</Tab>
                  <Tab>Headings</Tab>
                  <Tab>Spacing</Tab>
                </TabList>

                <TabPanels>
                  {/* Normal Text Tab */}
                  <TabPanel>
                    <VStack spacing={4} align="stretch">
                      <Heading size="sm" color="gray.700">
                        Normal Text Style
                      </Heading>
                      {renderFontStyleEditor(
                        "normal_text",
                        editedFontStyles.normal_text
                      )}
                    </VStack>
                  </TabPanel>

                  {/* Headings Tab */}
                  <TabPanel>
                    <VStack spacing={6} align="stretch">
                      {[
                        "heading_1",
                        "heading_2",
                        "heading_3",
                        "heading_4",
                        "heading_5",
                        "heading_6",
                      ].map((heading, index) => (
                        <Box key={heading}>
                          <Heading size="sm" color="gray.700" mb={3}>
                            Heading {index + 1}
                          </Heading>
                          {editedFontStyles[heading] ? (
                            renderFontStyleEditor(
                              heading,
                              editedFontStyles[heading]
                            )
                          ) : (
                            <Text
                              fontSize="sm"
                              color="gray.500"
                              fontStyle="italic"
                            >
                              No styles defined for this heading level
                            </Text>
                          )}
                          {index < 5 && <Divider mt={4} />}
                        </Box>
                      ))}
                    </VStack>
                  </TabPanel>

                  {/* Spacing Tab */}
                  <TabPanel>
                    <VStack spacing={4} align="stretch">
                      <Heading size="sm" color="gray.700">
                        Default Line Spacing
                      </Heading>
                      <FormControl>
                        <FormLabel fontSize="sm">
                          Default Line Spacing
                        </FormLabel>
                        <NumberInput
                          size="sm"
                          value={editedFontStyles.default_line_spacing || 1.15}
                          min={0.5}
                          max={3}
                          step={0.05}
                          onChange={(valueString) =>
                            setEditedFontStyles((prev: any) => ({
                              ...prev,
                              default_line_spacing:
                                Number.parseFloat(valueString) || 1.15,
                            }))
                          }
                          variant="outline"
                          clampValueOnBlur={false}
                        >
                          <NumberInputField
                            bg="white"
                            _hover={{ bg: "white" }}
                          />
                          <NumberInputStepper bg="transparent">
                            <NumberIncrementStepper
                              bg="transparent"
                              color="primary.500"
                              _hover={{
                                color: "primary.600",
                                bg: "primary.100",
                              }}
                              _active={{
                                color: "primary.700",
                                bg: "transparent",
                              }}
                              _disabled={{ opacity: 0.4, bg: "transparent" }}
                            />
                            <NumberDecrementStepper
                              bg="transparent"
                              color="primary.500"
                              _hover={{
                                color: "primary.600",
                                bg: "primary.100",
                              }}
                              _active={{
                                color: "primary.700",
                                bg: "transparent",
                              }}
                              _disabled={{ opacity: 0.4, bg: "transparent" }}
                            />
                          </NumberInputStepper>
                        </NumberInput>
                      </FormControl>

                      <Divider />

                      <Heading size="sm" color="gray.700">
                        Paragraph Spacing
                      </Heading>
                      <HStack spacing={4}>
                        <FormControl>
                          <FormLabel fontSize="sm">Before (pt)</FormLabel>
                          <NumberInput
                            size="sm"
                            value={
                              editedFontStyles.paragraph_spacing?.before || 0
                            }
                            min={0}
                            max={72}
                            onChange={(valueString) =>
                              setEditedFontStyles((prev: any) => ({
                                ...prev,
                                paragraph_spacing: {
                                  ...prev.paragraph_spacing,
                                  before: Number.parseFloat(valueString) || 0,
                                },
                              }))
                            }
                            variant="outline"
                            clampValueOnBlur={false}
                          >
                            <NumberInputField
                              bg="white"
                              _hover={{ bg: "white" }}
                            />
                            <NumberInputStepper bg="transparent">
                              <NumberIncrementStepper
                                bg="transparent"
                                color="primary.500"
                                _hover={{
                                  color: "primary.600",
                                  bg: "primary.100",
                                }}
                                _active={{
                                  color: "primary.700",
                                  bg: "transparent",
                                }}
                                _disabled={{ opacity: 0.4, bg: "transparent" }}
                              />
                              <NumberDecrementStepper
                                bg="transparent"
                                color="primary.500"
                                _hover={{
                                  color: "primary.600",
                                  bg: "primary.100",
                                }}
                                _active={{
                                  color: "primary.700",
                                  bg: "transparent",
                                }}
                                _disabled={{ opacity: 0.4, bg: "transparent" }}
                              />
                            </NumberInputStepper>
                          </NumberInput>
                        </FormControl>

                        <FormControl>
                          <FormLabel fontSize="sm">After (pt)</FormLabel>
                          <NumberInput
                            size="sm"
                            value={
                              editedFontStyles.paragraph_spacing?.after || 0
                            }
                            min={0}
                            max={72}
                            onChange={(valueString) =>
                              setEditedFontStyles((prev: any) => ({
                                ...prev,
                                paragraph_spacing: {
                                  ...prev.paragraph_spacing,
                                  after: Number.parseFloat(valueString) || 0,
                                },
                              }))
                            }
                            variant="outline"
                            clampValueOnBlur={false}
                          >
                            <NumberInputField
                              bg="white"
                              _hover={{ bg: "white" }}
                            />
                            <NumberInputStepper bg="transparent">
                              <NumberIncrementStepper
                                bg="transparent"
                                color="primary.500"
                                _hover={{
                                  color: "primary.600",
                                  bg: "primary.100",
                                }}
                                _active={{
                                  color: "primary.700",
                                  bg: "transparent",
                                }}
                                _disabled={{ opacity: 0.4, bg: "transparent" }}
                              />
                              <NumberDecrementStepper
                                bg="transparent"
                                color="primary.500"
                                _hover={{
                                  color: "primary.600",
                                  bg: "primary.100",
                                }}
                                _active={{
                                  color: "primary.700",
                                  bg: "transparent",
                                }}
                                _disabled={{ opacity: 0.4, bg: "transparent" }}
                              />
                            </NumberInputStepper>
                          </NumberInput>
                        </FormControl>
                      </HStack>
                    </VStack>
                  </TabPanel>
                </TabPanels>
              </Tabs>
            )}
          </ModalBody>

          <ModalFooter>
            <Button
              variant="ghost"
              mr={3}
              onClick={onFontEditClose}
              isDisabled={savingFontStyles}
              colorScheme="gray"
              _hover={{ bg: "gray.100" }}
              color="gray.600"
            >
              Cancel
            </Button>
            <Button
              colorScheme="primary"
              onClick={saveFontStyles}
              isLoading={savingFontStyles}
              loadingText="Saving..."
            >
              Save Changes
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
